/**
 * One metric card that splits a whole into parts: a figure and an aside on one
 * line, one segmented bar, then a legend with each part's count. Used by the
 * Dashboard's idea cards (status, verdict, industry, my ideas).
 *
 * A legend entry can do one of three things:
 * - `onSelect`: toggle a filter on this page (a pressed button; selecting the
 *   only selected part again clears it);
 * - `linkFor`: open another page filtered to that part (a link; a part it
 *   returns null for stays plain text);
 * - neither: plain text.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { formatInt, share } from '@/ui/format';

export type SplitPart<K extends string> = { key: K; label: string; count: number; seg: string };

type Interaction<K extends string> =
  | { onSelect: (value: K[]) => void; selected: K[]; linkFor?: never; linkTitle?: never }
  | { linkFor: (key: K) => string | null; linkTitle?: string; onSelect?: never; selected?: never }
  | { onSelect?: never; selected?: never; linkFor?: never; linkTitle?: never };

export function SplitCard<K extends string>({
  label,
  figure,
  noun,
  aside,
  parts,
  className,
  note,
  ...interaction
}: {
  label: string;
  figure: number;
  noun: string;
  aside?: ReactNode;
  parts: SplitPart<K>[];
  className?: string;
  /** A line under the legend. */
  note?: ReactNode;
} & Interaction<K>) {
  const whole = parts.reduce((sum, p) => sum + p.count, 0);

  const entry = (p: SplitPart<K>) => {
    const content = (
      <>
        <span className={`swatch ${p.seg}`} />
        {p.label} <b>{formatInt(p.count)}</b>
      </>
    );
    if (interaction.onSelect) {
      const { onSelect, selected } = interaction;
      const only = selected.length === 1 && selected[0] === p.key;
      return (
        <button
          type="button"
          className="legend-filter"
          aria-pressed={only}
          title={only ? 'Show every idea' : `Show only ${p.label.toLowerCase()} ideas`}
          onClick={() => onSelect(only ? [] : [p.key])}
        >
          {content}
        </button>
      );
    }
    const to = interaction.linkFor?.(p.key);
    if (to) {
      return (
        <Link to={to} className="legend-filter" title={interaction.linkTitle ?? 'Open in Business Opportunities'}>
          {content}
        </Link>
      );
    }
    return content;
  };

  return (
    <section className={`metric-tile usage-card ${className ?? ''}`} aria-label={label}>
      <div className="usage-card-head">
        <div className="usage-card-figure">
          <span className="metric-value">{formatInt(figure)}</span>
          <span className="usage-card-of">{noun}</span>
        </div>
        {aside}
      </div>
      <div
        className="stack-bar split-bar"
        role="img"
        aria-label={parts.map((p) => `${p.count} ${p.label.toLowerCase()}`).join(', ') || 'Nothing yet'}
      >
        {parts.map((p) => (
          <span key={p.key} className={`seg ${p.seg}`} style={{ width: share(p.count, whole) }} />
        ))}
      </div>
      <ul className="usage-legend">
        {parts.map((p) => (
          <li key={p.key}>{entry(p)}</li>
        ))}
      </ul>
      {note && <p className="usage-card-note">{note}</p>}
    </section>
  );
}
